<template>
  <div
    v-if="tiles.length && isAbTestActive"
    class="quick-links filter-tile-strip"
    :class="{
      'quick-links--start': arrivedState.left,
      'quick-links--end': arrivedState.right,
    }"
  >
    <h3 class="quick-links__heading heading heading--5">
      {{ heading }}
    </h3>

    <div class="quick-links__container">
      <button
        type="button"
        class="quick-links__prev"
        @click="showPrev"
      >
        <IconArrow class="icon icon--arrow icon--arrow-left" />
      </button>

      <ul
        ref="tilesEl"
        class="quick-links__list"
      >
        <li
          v-for="option in tiles"
          :key="option.key"
          class="quick-links__item"
        >
          <button
            type="button"
            class="quick-links__button button"
            :class="{ 'filter-tile-strip__button--active': option.active }"
            @click="applyOption(option)"
          >
            <span>{{ option.label }}</span>
          </button>
        </li>
      </ul>

      <button
        type="button"
        class="quick-links__next"
        @click="showNext"
      >
        <IconArrow class="icon icon--arrow icon--arrow-right" />
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
type TileOption = {
  key: string
  label: string
  active?: boolean
}

const props = defineProps<{
  heading: string
  tiles: TileOption[]
  filterKey: string
}>()

const route = useRoute()
const router = useRouter()
const { filtersApply } = useDataLayer()
const { getBrandUrl } = useBrands()
const tilesEl = ref<HTMLElement | null>(null)
const { x, arrivedState, measure } = useScroll(tilesEl)

const isAbTestActive = ref(false)
const bodyEl = ref<HTMLElement | null>(null)

const syncAbTestState = () => {
  isAbTestActive.value = !!bodyEl.value?.classList.contains('ab-216530')
}

onMounted(() => {
  bodyEl.value = document.body
  syncAbTestState()
})

useMutationObserver(bodyEl, syncAbTestState, { attributeFilter: ['class'] })

useResizeObserver(tilesEl, () => measure())

watch(isAbTestActive, async (active) => {
  if (!active) return

  await nextTick()
  measure()
})

const basePath = computed(() => {
  const currentPath = route.path.endsWith('/') ? route.path : `${route.path}/`

  if (props.filterKey !== 'tra_brand') {
    return currentPath
  }

  const activeBrand = props.tiles.find(option => option.active)
  const activeBrandUrl = activeBrand ? getBrandUrl(activeBrand.key) : null

  if (!activeBrandUrl) {
    return currentPath
  }

  const normalizedBrandUrl = `/${activeBrandUrl.replace(/^\/|\/$/g, '')}/`
  return currentPath.endsWith(normalizedBrandUrl)
    ? currentPath.slice(0, -normalizedBrandUrl.length + 1) || '/'
    : currentPath
})

const applyOption = async (option: TileOption) => {
  filtersApply([
    {
      label: props.heading,
      value: option.label,
      action: option.active ? 'remove' : 'add',
    },
  ])

  // Keep filters that are already active (other tile strips, regular filters)
  // and only replace the value for this strip's own filter. Paging restarts.
  const { page, ...currentQuery } = route.query

  await router.push({
    path: basePath.value,
    query: {
      ...currentQuery,
      filter: '1',
      [props.filterKey]: option.key,
    },
  })
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const showNext = () => {
  x.value = x.value + (tilesEl.value?.clientWidth || 0)
}

const showPrev = () => {
  x.value = x.value - (tilesEl.value?.clientWidth || 0)
}
</script>

<style>
@import url("~layers/app/components/Filter/TileStrip.css");
</style>
